// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { DateTime, DateTimeUnit } from "luxon";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "react-bootstrap";

import {
  ActionGraphCommand,
  ActionType,
  IAction,
  IActionTypeSetPeriod,
  raiseAction,
} from "actions";
import { IState } from "states/states";
import { DataState, PeriodIdentfier, TimePeriod } from "types";
import { isSet } from "shared/utils/utils";
import { IPeriodData, ModalPanel } from "states/guistates";
import {
  getLocalDate,
  getDisplayDate,
  getDateRangeFromPeriod,
  getDeletionBoundary,
  getIdentifierForMainPanel,
  getPeriodStart,
  getUpdateCommandForIdentifier,
  isSafariBrowser,
  getAggregationTimeUnitUINames,
} from "guiutils";
import TrendWindowModel from "model/trendwindow";
import { Aggregation } from "graphql/api";
import InlineDatePicker from "view/comp/InlineDatePicker";

import "./ModalFilter.scss";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface IModalTimeFilterProps {
  period: IPeriodData;
  isOpen: boolean;
  dataState: DataState;
  identifier: PeriodIdentfier;
  dataStartDate: number | undefined;
  // start (s) before which trend data is only kept at monthly resolution - re-aggregation
  // discards finer buckets past this point (see getDeletionBoundary); undefined for
  // chargingSessionFilter, which isn't subject to this retention policy
  aggregationBoundary: number | undefined;
}

const mapStateToProps = (state: IState) => {
  const identifier = getIdentifierForMainPanel(state.router.panel);
  const period = state.gui[identifier];

  const minTimestamp = TrendWindowModel.getDataBoundaries(state, identifier).min;

  // the date pickers cannot go before the day of the earliest data point
  const dataStartDate =
    isSet(minTimestamp) ?
      getPeriodStart(minTimestamp, TimePeriod.DAY, state.site.tzOffset ?? 0)
    : undefined;

  // mirrors the boundary guiReducer applies when a period is actually accepted (see
  // deriveNewGraphStartDate) - kept purely for the modal's own preview/summary
  const aggregationBoundary =
    identifier === "chargingSessionFilter" ? undefined : (
      getDeletionBoundary(state).aggregationBoundary?.toSeconds()
    );

  const props: IModalTimeFilterProps = {
    period,
    isOpen: state.gui.modal.panel === ModalPanel.PERIOD_SELECTION,
    dataState: state.gui.modal.dataState,
    dataStartDate,
    identifier,
    aggregationBoundary,
  };

  return props;
};

interface IQuickTimeRange {
  // translation id, e.g. "TODAY" -> see src/view/_dummy_translations.tsx
  id: string;
  compute: () => { start: number; end?: number; period: TimePeriod; customDaysSelected?: number };
}

// quick-selection grid entries (renderQuickEntries) - a flat list of exactly 8 (2 full rows of
// 4, or 3 on the controller without year), so it always fills the grid evenly regardless of
// column count; see .quickSelectGrid.
const QUICK_TIME_RANGES: IQuickTimeRange[] = [
  {
    id: "TODAY",
    compute: () => ({ start: DateTime.now().startOf("day").valueOf(), period: TimePeriod.DAY }),
  },
  {
    id: "THISWEEK",
    compute: () => ({
      start: DateTime.now().startOf("week").startOf("day").valueOf(),
      period: TimePeriod.WEEK,
    }),
  },
  {
    id: "THISMONTH",
    compute: () => ({
      start: DateTime.now().startOf("month").startOf("day").valueOf(),
      period: TimePeriod.MONTH,
    }),
  },
  {
    id: "THISYEAR",
    compute: () => ({
      start: DateTime.now().startOf("year").startOf("day").valueOf(),
      period: TimePeriod.YEAR,
    }),
  },
  {
    id: "YESTERDAY",
    compute: () => ({
      start: DateTime.now().minus({ days: 1 }).startOf("day").valueOf(),
      end: DateTime.now().startOf("day").valueOf(),
      period: TimePeriod.DAY,
    }),
  },
  {
    id: "LASTWEEK",
    compute: () => ({
      start: DateTime.now().minus({ weeks: 1 }).startOf("week").valueOf(),
      end: DateTime.now().startOf("week").valueOf(),
      period: TimePeriod.WEEK,
    }),
  },
  {
    id: "LASTMONTH",
    compute: () => ({
      start: DateTime.now().minus({ months: 1 }).startOf("month").valueOf(),
      end: DateTime.now().startOf("month").valueOf(),
      period: TimePeriod.MONTH,
    }),
  },
  {
    id: "LASTYEAR",
    compute: () => ({
      start: DateTime.now().minus({ year: 1 }).startOf("year").valueOf(),
      end: DateTime.now().startOf("year").valueOf(),
      period: TimePeriod.YEAR,
    }),
  },
];

// the period-type buttons offered below "Quick selection" - order also determines display order
const PERIOD_TYPES = [
  TimePeriod.DAY,
  TimePeriod.WEEK,
  TimePeriod.MONTH,
  TimePeriod.YEAR,
  TimePeriod.CUSTOM,
] as const;

// the controller offers fixed periods up to a month only, to keep its load low
const OFFERED_PERIOD_TYPES = PERIOD_TYPES.filter(
  (period) => IS_CLOUD || (period !== TimePeriod.YEAR && period !== TimePeriod.CUSTOM),
);
const OFFERED_QUICK_TIME_RANGES = QUICK_TIME_RANGES.filter((range) =>
  (OFFERED_PERIOD_TYPES as TimePeriod[]).includes(range.compute().period),
);
// columns of the quick-selection and period-type grids: one per offered fixed period
const GRID_COLUMNS = OFFERED_PERIOD_TYPES.filter((period) => period !== TimePeriod.CUSTOM).length;
const GRID_STYLE = { "--grid-columns": GRID_COLUMNS } as React.CSSProperties;

const PERIOD_TYPE_LABELS: Record<(typeof PERIOD_TYPES)[number], string> = {
  [TimePeriod.DAY]: "day",
  [TimePeriod.WEEK]: "week",
  [TimePeriod.MONTH]: "month",
  [TimePeriod.YEAR]: "year",
  [TimePeriod.CUSTOM]: "custom",
};

// luxon startOf() unit used to align a selected date to the start of its period; CUSTOM (and
// ALL, unused here) have no alignment of their own
const PERIOD_ALIGN_UNIT: Partial<Record<TimePeriod, DateTimeUnit>> = {
  [TimePeriod.DAY]: "day",
  [TimePeriod.WEEK]: "week",
  [TimePeriod.MONTH]: "month",
  [TimePeriod.YEAR]: "year",
};

const messages = defineMessages({
  endDateMustBeAfterStart: {
    id: "End date must be after start date",
  },
  endDateRequired: {
    id: "End date is required",
  },
  startDateRequired: {
    id: "Start date is required",
  },
  dateRequired: {
    id: "Date is required",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  onlyDatesUntil: { date: string };
  startDateMustBeAfter: { date: string };
}>({
  onlyDatesUntil: {
    id: "Only dates until {date} are allowed",
  },
  startDateMustBeAfter: {
    id: "Start date must be {date} or later",
  },
});

interface IModalTimeFilterState {
  startDate: number | undefined;
  endDate: number | undefined; // only tracked while timePeriod === CUSTOM
  startDateInput: string; // "YYYY-MM-DD" (day/week), "YYYY-MM" (month) or "YYYY" (year/custom-start)
  endDateInput: string; // only used while timePeriod === CUSTOM
  startDateError: string | undefined;
  endDateError: string | undefined;
  timePeriod: TimePeriod;
  // the last date the user actually picked/typed (day-granular, never itself rounded to a
  // period boundary) - used as the anchor when switching period type buttons, instead of startDate.
  // which is rounded and set to the periods begin (e.g. year), so the date would run backwards
  anchorDate: number | undefined;
}

interface IModalTimeFilterDispatch {
  onCancelClick: () => void;
  onFilterChanged: (newFilter: IActionTypeSetPeriod) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalTimeFilterDispatch = {
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
    onFilterChanged: (newFilter: IActionTypeSetPeriod) => {
      // set new date filter
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, newFilter));
      // load new graph data
      const updateCmd = getUpdateCommandForIdentifier(newFilter.identifier);
      dispatch(raiseAction(updateCmd));
    },
  };
  return dispatchProps;
};

class ModalTimeFilterComponent extends React.Component<
  IModalTimeFilterProps & IModalTimeFilterDispatch,
  IModalTimeFilterState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IModalTimeFilterState = this.getStateFromPeriod();

  componentDidUpdate(prevProps: IModalTimeFilterProps) {
    const justOpened = !prevProps.isOpen && this.props.isOpen;
    if (prevProps.period !== this.props.period || justOpened) {
      this.setState(this.getStateFromPeriod());
    }
  }

  // The modal edits the raw selection (selectedDate + timePeriod), not the timezone-adjusted
  // graph boundary, so getDateRangeFromPeriod is the right derivation here.
  private getStateFromPeriod(): IModalTimeFilterState {
    const { startDate, endDate, timePeriod } = getDateRangeFromPeriod(this.props.period);
    const isCustom = timePeriod === TimePeriod.CUSTOM;
    return {
      startDate,
      endDate: isCustom ? endDate : undefined,
      startDateInput:
        isCustom ?
          getLocalDate(startDate * 1000)
        : this.formatInputForPeriod(timePeriod, startDate),
      endDateInput: isCustom ? this.getLastDay(endDate) : "",
      startDateError: undefined,
      endDateError: undefined,
      timePeriod,
      anchorDate: startDate,
    };
  }

  // Formats a period's start date for the single-field pickers (day/week share the plain
  // "YYYY-MM-DD" format - week just always displays that week's Monday).
  private formatInputForPeriod(period: TimePeriod, startDate: number): string {
    const dt = DateTime.fromSeconds(startDate);
    if (period === TimePeriod.MONTH) return dt.toFormat("yyyy-MM");
    if (period === TimePeriod.YEAR) return dt.toFormat("yyyy");
    return getLocalDate(startDate * 1000);
  }

  // Validation methods. alignUnit must match whatever unit startDate itself was aligned to
  // (e.g. "year" for a selected year) - otherwise a period that merely starts before the data's
  // start day, but still contains it (like the year or month the data starts in), would be
  // wrongly flagged as entirely out of range.
  private validateStartDate(startDate: number | undefined, alignUnit: DateTimeUnit = "day") {
    if (!isSet(startDate)) {
      return undefined;
    }
    const tomorrowStart = this.getStartOfNextDay(DateTime.now());
    const todayFormatted = getDisplayDate(Date.now(), this.context.locale);
    if (startDate >= tomorrowStart) {
      return this.context.formatMessage(valueMessages.onlyDatesUntil, { date: todayFormatted });
    }
    if (isSet(this.props.dataStartDate)) {
      const dataStartAligned = Math.round(
        DateTime.fromSeconds(this.props.dataStartDate).startOf(alignUnit).toSeconds(),
      );
      if (startDate < dataStartAligned) {
        const minDate = getDisplayDate(this.props.dataStartDate * 1000, this.context.locale);
        return this.context.formatMessage(valueMessages.startDateMustBeAfter, { date: minDate });
      }
    }
    return undefined;
  }

  private validateEndDate(endDate: number | undefined, startDate: number | undefined) {
    if (!isSet(endDate)) {
      return undefined;
    }
    const tomorrowStart = this.getStartOfNextDay(DateTime.now());
    const todayFormatted = getDisplayDate(Date.now(), this.context.locale);
    if (endDate > tomorrowStart) {
      return this.context.formatMessage(valueMessages.onlyDatesUntil, { date: todayFormatted });
    }
    if (isSet(startDate) && endDate <= startDate) {
      return this.context.formatMessage(messages.endDateMustBeAfterStart);
    }
    return undefined;
  }

  // Date ranges are half-open [startDate, endDate): the end is the next day at 00:00
  private getStartOfNextDay(dt: DateTime): number {
    return Math.round(dt.startOf("day").plus({ days: 1 }).toSeconds());
  }
  // Local "YYYY-MM-DD" of the last day included in a range ending at the exclusive endDate.
  private getLastDay(endDate: number | undefined): string {
    return isSet(endDate) ? getLocalDate((endDate - 1) * 1000) : "";
  }

  // Parses a "YYYY-MM-DD" string the same way the native date-input's valueAsDate would
  // (UTC midnight), so browsers without a usable date picker (Safari) can fall back to a
  // plain text input without changing downstream date handling.
  private parseIsoDateInput(value: string): Date | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return null;
    }
    const dt = DateTime.fromISO(value, { zone: "utc" });
    return dt.isValid ? dt.toJSDate() : null;
  }

  // Only flags text as invalid once it reaches full length but still doesn't parse - a
  // partially-typed date (e.g. "2026-07" for a day field) isn't an error yet, just mid-entry.
  private isInvalidDateText(text: string): boolean {
    return text.length === 10 && this.parseIsoDateInput(text) === null;
  }

  // Same idea as isInvalidDateText, but for the single-field day/week/month/year pickers.
  private isInvalidPeriodDateText(text: string, period: TimePeriod): boolean {
    const expectedLength =
      period === TimePeriod.YEAR ? 4
      : period === TimePeriod.MONTH ? 7
      : 10;
    return text.length === expectedLength && this.parsePeriodDateInput(text, period) === null;
  }

  private parsePeriodDateInput(value: string, period: TimePeriod): Date | null {
    // built as UTC midnight (like parseIsoDateInput below) so every typed-text path shares the
    // same anchor - applyPeriodDate relies on that to tell them apart from calendar/grid picks
    if (period === TimePeriod.MONTH) {
      if (!/^\d{4}-\d{2}$/.test(value)) return null;
      const [year, month] = value.split("-").map(Number);
      if (month < 1 || month > 12) return null;
      const dt = DateTime.fromObject({ year, month, day: 1 }, { zone: "utc" });
      return dt.isValid ? dt.toJSDate() : null;
    }
    if (period === TimePeriod.YEAR) {
      if (!/^\d{4}$/.test(value)) return null;
      const dt = DateTime.fromObject({ year: Number(value), month: 1, day: 1 }, { zone: "utc" });
      return dt.isValid ? dt.toJSDate() : null;
    }
    // day & week both use the plain "YYYY-MM-DD" native date format
    return this.parseIsoDateInput(value);
  }

  // parsedDate is UTC midnight from typed text/native input (parseIsoDateInput /
  // valueAsDate), but local midnight from the Safari-fallback InlineDatePicker's calendar
  // grid - sourceZone tells the two apart so the right calendar fields get extracted (reading
  // the wrong one shifts the day in positive/negative UTC-offset zones, see applyPeriodDate)
  private applyStartDate(parsedDate: Date, rawInputValue: string, sourceZone: "utc" | "local") {
    const [year, month, day] =
      sourceZone === "utc" ?
        [parsedDate.getUTCFullYear(), parsedDate.getUTCMonth() + 1, parsedDate.getUTCDate()]
      : [parsedDate.getFullYear(), parsedDate.getMonth() + 1, parsedDate.getDate()];
    const startDate = Math.round(DateTime.local(year, month, day).startOf("day").toSeconds());

    // keep the existing end date if still valid, otherwise it's now invalid (not "cleared") -
    // the end date is required, so flag it rather than silently falling back to open-ended
    const endDate =
      this.state.endDate && this.state.endDate > startDate ? this.state.endDate : undefined;

    this.setState({
      startDate,
      endDate,
      startDateInput: rawInputValue,
      endDateInput: this.getLastDay(endDate),
      startDateError: this.validateStartDate(startDate),
      endDateError:
        isSet(endDate) ?
          this.validateEndDate(endDate, startDate)
        : this.context.formatMessage(messages.endDateRequired),
      anchorDate: startDate,
    });
  }

  // see applyStartDate's doc comment - same UTC-vs-local ambiguity applies here
  private applyEndDate(parsedDate: Date, rawInputValue: string, sourceZone: "utc" | "local") {
    const [year, month, day] =
      sourceZone === "utc" ?
        [parsedDate.getUTCFullYear(), parsedDate.getUTCMonth() + 1, parsedDate.getUTCDate()]
      : [parsedDate.getFullYear(), parsedDate.getMonth() + 1, parsedDate.getDate()];
    const endDate = this.getStartOfNextDay(DateTime.local(year, month, day));

    // Validate the full date (including checking if future)
    const endDateError = this.validateEndDate(endDate, this.state.startDate);

    // Store the end date (cap at today if in future, but keep the input value)
    const tomorrowStart = this.getStartOfNextDay(DateTime.now());
    const safeEndDate = endDate > tomorrowStart ? tomorrowStart : endDate;

    this.setState({
      endDate: safeEndDate,
      endDateInput: rawInputValue,
      endDateError,
    });
  }

  // Applies a date picked (or typed) for the single-field day/week/month/year pickers -
  // aligning it to the start of the currently selected period type. rawInputValue is kept
  // verbatim while the user is typing (so we don't fight their cursor); when picked from a
  // calendar/grid it is undefined and the input is reformatted from the aligned date instead.
  private applyPeriodDate(parsedDate: Date, rawInputValue: string | undefined) {
    const period = this.state.timePeriod;
    const alignUnit = PERIOD_ALIGN_UNIT[period] ?? "day";

    // typed/native input is normalized to UTC midnight (see parsePeriodDateInput /
    // parseIsoDateInput), but InlineDatePicker's calendar and month/year grids (rawInputValue
    // undefined here) hand back a Date at *local* midnight instead - read the calendar fields
    // with the getters matching that source, or the day/month shifts backward in positive
    // UTC-offset time zones
    const [year, month, day] =
      rawInputValue === undefined ?
        [parsedDate.getFullYear(), parsedDate.getMonth() + 1, parsedDate.getDate()]
      : [parsedDate.getUTCFullYear(), parsedDate.getUTCMonth() + 1, parsedDate.getUTCDate()];
    const localDate = DateTime.local(year, month, day);
    const startDate = Math.round(localDate.startOf(alignUnit).toSeconds());

    this.setState({
      startDate,
      startDateInput: rawInputValue ?? this.formatInputForPeriod(period, startDate),
      startDateError: this.validateStartDate(startDate, alignUnit),
      anchorDate: startDate,
    });
  }

  private selectPeriodType(period: TimePeriod) {
    if (period === this.state.timePeriod) return;

    // deliberately anchorDate, not startDate - see the field's doc comment for why re-deriving
    // from the already period-aligned startDate on every switch causes drift
    const anchor = this.state.anchorDate ?? Math.round(DateTime.now().toSeconds());

    if (period === TimePeriod.CUSTOM) {
      // prefill both fields from the period being left, rather than starting blank - the end
      // date is required, so there's no valid "empty" state to start from anyway. Capped at
      // today since e.g. the current, still-ongoing week's implicit end is in the future.
      const priorRange = getDateRangeFromPeriod({
        selectedDate: anchor,
        timePeriod: this.state.timePeriod,
      });
      const tomorrowStart = this.getStartOfNextDay(DateTime.now());
      // same reasoning as the non-custom branch below - the prior period's start (e.g. a
      // month's 1st) can predate the data itself
      const startDate =
        isSet(this.props.dataStartDate) ?
          Math.max(priorRange.startDate, this.props.dataStartDate)
        : priorRange.startDate;
      const endDate = Math.min(priorRange.endDate ?? tomorrowStart, tomorrowStart);

      this.setState({
        timePeriod: period,
        startDate,
        endDate,
        startDateInput: getLocalDate(startDate * 1000),
        endDateInput: this.getLastDay(endDate),
        startDateError: this.validateStartDate(startDate),
        endDateError: this.validateEndDate(endDate, startDate),
      });
      return;
    }

    const alignUnit = PERIOD_ALIGN_UNIT[period] ?? "day";
    let startDate = Math.round(DateTime.fromSeconds(anchor).startOf(alignUnit).toSeconds());

    // the anchor carries over from whatever period was previously selected (e.g. a month
    // picked right at the data's start), so re-aligning it to a finer unit (week/day) can land
    // before the data actually begins - snap forward to the earliest period that has data
    // instead of landing on a startDate that's immediately flagged as out of range
    if (isSet(this.props.dataStartDate)) {
      const dataStartAligned = Math.round(
        DateTime.fromSeconds(this.props.dataStartDate).startOf(alignUnit).toSeconds(),
      );
      startDate = Math.max(startDate, dataStartAligned);
    }

    this.setState({
      timePeriod: period,
      startDate,
      endDate: undefined,
      startDateInput: this.formatInputForPeriod(period, startDate),
      endDateInput: "",
      startDateError: this.validateStartDate(startDate, alignUnit),
      endDateError: undefined,
    });
  }

  public render() {
    if (!this.props.isOpen) {
      return <></>; // avoid rendering of inexisting data
    }

    // Button is enabled if startDate is valid, and - for Custom - endDate is also valid and
    // after startDate (Day/Week/Month/Year have no separate end date to check).
    const isButtonEnabled = this.canEnableUseFilterButton();

    return (
      <Modal
        className="modal_filter modal_filter-time"
        show={this.props.isOpen}
        onHide={this.props.onCancelClick}
      >
        <ModalHeader></ModalHeader>
        <ModalBody>
          <>
            <div className="headline">
              <h5>
                <FormattedMessage id="Time Limitations" />
              </h5>
              <div>
                <FormattedMessage id="Select the time-range you want to see." />
              </div>
            </div>
            <div className="filterSection">
              <p>
                <FormattedMessage id="Quick selection" />
              </p>
              {this.renderQuickEntries()}
            </div>
            <div className="filterSection">
              <p>
                <FormattedMessage id="Select period" />
              </p>
              {this.renderPeriodTypeGrid()}
              {IS_CLOUD && this.renderCustomPeriodButton()}
              {this.renderDateFields()}
              {this.renderSummary()}
            </div>
          </>
        </ModalBody>
        <ModalFooter style={{ justifyContent: "center" }}>
          <Button
            id="cancelTrend"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            id="useFilter"
            variant="primary"
            onClick={() => {
              this.activateCustomFilter();
            }}
            disabled={!isButtonEnabled}
          >
            <FormattedMessage id="Accept" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  // Rendering methods
  private renderColumnConnectorLines() {
    const leftPercents = Array.from(
      { length: GRID_COLUMNS - 1 },
      (_item, index) => ((index + 1) * 100) / GRID_COLUMNS,
    );
    return leftPercents.map((leftPercent) => (
      <div
        key={`connector_${leftPercent}`}
        className="gridConnectorLine"
        style={{ left: `${leftPercent}%` }}
      />
    ));
  }

  private canEnableUseFilterButton() {
    const hasStartDateError = this.state.startDateError !== undefined;
    const hasEndDateError = this.state.endDateError !== undefined;

    return (
      this.props.dataState !== DataState.SAVE_REQ &&
      !hasStartDateError &&
      !hasEndDateError &&
      isSet(this.state.startDate) &&
      // an end date is required for Custom - both must be valid, with proper ordering
      (this.state.timePeriod !== TimePeriod.CUSTOM ||
        (isSet(this.state.endDate) && this.state.endDate > this.state.startDate))
    );
  }

  private activateCustomFilter() {
    if (isSet(this.state.startDate) === false) {
      return;
    }
    const period = this.state.timePeriod;

    let customDaysSelected: number | undefined;
    if (period === TimePeriod.CUSTOM) {
      const start = DateTime.fromSeconds(this.state.startDate);
      const end = DateTime.fromSeconds(
        this.state.endDate ?? this.getStartOfNextDay(DateTime.now()),
      );
      // number of days for a custom period (rounded: a DST day is 23 or 25 hours long)
      customDaysSelected = Math.round(end.diff(start).as("days"));
    }

    this.props.onFilterChanged({
      identifier: this.props.identifier,
      command: ActionGraphCommand.SET_DATE,
      selectedDate: this.state.startDate,
      customDaysSelected,
      timePeriod: period,
    });
  }

  private renderPeriodTypeButton(period: (typeof PERIOD_TYPES)[number]) {
    return (
      <button
        type="button"
        className={`btn btn-sm ${this.state.timePeriod === period ? "btn-primary" : "btn-outline-primary"} btn-periodtype`}
        onClick={() => this.selectPeriodType(period)}
      >
        <FormattedMessage id={`TimePeriod_${PERIOD_TYPE_LABELS[period]}`} />
      </button>
    );
  }

  // Custom isn't part of the Day/Week/Month/Year grid above (it has no counterpart in the
  // quick-selection grid to align with), so it's a plain link instead of a same-size peer
  // button - marking it as the advanced/fallback option rather than a 5th grid entry.
  private renderCustomPeriodButton() {
    const isActive = this.state.timePeriod === TimePeriod.CUSTOM;
    return (
      <div className="customPeriodLinkRow">
        <button
          type="button"
          className={`customPeriodLink ${isActive ? "active" : ""}`}
          onClick={() => this.selectPeriodType(TimePeriod.CUSTOM)}
        >
          <FormattedMessage id="Or select a custom date range" />
        </button>
      </div>
    );
  }

  // Day/Week/Month/Year line up with the 4 columns of the quick-selection grid above (same
  // table layout + connector lines); Custom doesn't fit that grid, so it's rendered separately.
  // CSS grid (not a <table>) so the column count can reflow via media query on small screens -
  // the connector lines are absolutely positioned, so they don't consume grid cells themselves.
  private renderPeriodTypeGrid() {
    const gridTypes = OFFERED_PERIOD_TYPES.filter((period) => period !== TimePeriod.CUSTOM);

    return (
      <div className="periodTypeButtons" style={GRID_STYLE}>
        {this.renderColumnConnectorLines()}
        {gridTypes.map((period) => (
          <div key={period} className="periodTypeCell">
            {this.renderPeriodTypeButton(period)}
          </div>
        ))}
      </div>
    );
  }

  // A closed-range entry (yesterday/last week/last month/last year) is pointless to offer once
  // its whole range falls before the earliest data point - e.g. a brand-new site has no
  // "last year" to show. Ongoing entries (today/this week/...) have no `end` and stay enabled,
  // since they always cover the present regardless of how little history exists.
  private isQuickRangeDisabled(range: IQuickTimeRange): boolean {
    const { end } = range.compute();
    return isSet(end) && isSet(this.props.dataStartDate) && end <= this.props.dataStartDate * 1000;
  }

  private renderQuickEntries() {
    return (
      <div className="quickSelectGrid" style={GRID_STYLE}>
        {this.renderColumnConnectorLines()}
        {OFFERED_QUICK_TIME_RANGES.map((entry) => {
          const disabled = this.isQuickRangeDisabled(entry);
          return (
            <div key={entry.id} id={`mtf_${entry.id}`} className="quickSelectCell">
              <button
                type="button"
                className="btn btn-noborder"
                disabled={disabled}
                onClick={() => this.prepareCurrentQuickFilter(entry)}
              >
                <FormattedMessage id={entry.id} />
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  private getSingleFieldLabelId(period: TimePeriod): string {
    switch (period) {
      case TimePeriod.WEEK:
        return "Select any date within the week";
      case TimePeriod.MONTH:
        return "Select month";
      case TimePeriod.YEAR:
        return "Select year";
      default:
        return "Select Date";
    }
  }

  // Renders the single date/month/year field used for the Day, Week, Month and Year period
  // types. Day & Week keep the existing native <input type="date"> (with the Safari-only
  // InlineDatePicker fallback); Month & Year always use InlineDatePicker's grid pickers, since
  // native <input type="month">/year inputs have inconsistent (or no) browser support.
  private renderSingleDateField() {
    const period = this.state.timePeriod;
    const granularity: "day" | "month" | "year" =
      period === TimePeriod.MONTH ? "month"
      : period === TimePeriod.YEAR ? "year"
      : "day";
    const useCustomPicker = granularity !== "day" || isSafariBrowser();

    const startDateMin = getLocalDate(
      this.props.dataStartDate ? this.props.dataStartDate * 1000 : undefined,
    );
    const startDateMax = getLocalDate(new Date());
    const startMinDate =
      isSet(this.props.dataStartDate) ? new Date(this.props.dataStartDate * 1000) : undefined;
    const todayMaxDate = new Date();

    const onTextChange = (inputValue: string) => {
      if (inputValue === "") {
        this.setState({
          startDate: undefined,
          startDateInput: "",
          startDateError: this.context.formatMessage(messages.dateRequired),
        });
        return;
      }
      const parsedDate = this.parsePeriodDateInput(inputValue, period);
      if (parsedDate === null) {
        this.setState({
          startDate: undefined,
          startDateInput: inputValue,
          startDateError: this.context.formatMessage(messages.dateRequired),
        });
        return;
      }
      this.applyPeriodDate(parsedDate, inputValue);
    };

    return (
      <div className="customTime singlePeriodDate">
        <span>
          <FormattedMessage id={this.getSingleFieldLabelId(period)} />
        </span>
        {useCustomPicker ?
          <InlineDatePicker
            id="periodDate"
            granularity={granularity}
            value={this.state.startDateInput}
            selectedDate={
              isSet(this.state.startDate) ? new Date(this.state.startDate * 1000) : undefined
            }
            minDate={startMinDate}
            maxDate={todayMaxDate}
            hasError={
              !this.state.startDate ||
              isSet(this.state.startDateError) ||
              this.isInvalidPeriodDateText(this.state.startDateInput, period)
            }
            onTextChange={onTextChange}
            onSelectDate={(date) => this.applyPeriodDate(date, undefined)}
            onClear={() => onTextChange("")}
          />
        : <input
            id="periodDate"
            type="date"
            className={`dateinput ${this.state.startDate ? "" : "error"}`}
            value={this.state.startDateInput}
            min={startDateMin}
            max={startDateMax}
            onChange={(event) => {
              const inputValue = event.currentTarget.value;
              if (inputValue === "") {
                onTextChange("");
                return;
              }
              const parsedDate = event.currentTarget.valueAsDate;
              if (parsedDate === null) {
                this.setState({ startDateInput: inputValue });
                return;
              }
              this.applyPeriodDate(parsedDate, inputValue);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && this.canEnableUseFilterButton()) {
                this.activateCustomFilter();
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            placeholder="YYYY-MM-DD"
          />
        }
        <div className="input-error-message">{this.state.startDateError || ""}</div>
      </div>
    );
  }

  private renderCustomDateFields() {
    // Safari's native date-picker steals focus while typing and its popup won't reliably
    // close (long-standing WebKit bugs). So on Safari we replace the native picker with our
    // own InlineDatePicker (text input + self-contained calendar dropdown), which we fully
    // control. Other browsers keep the native <input type="date">, which works fine there.
    const isSafari = isSafariBrowser();

    const startDateMin = getLocalDate(
      this.props.dataStartDate ? this.props.dataStartDate * 1000 : undefined,
    );
    const startDateMax = getLocalDate(new Date());
    const endDateMin = getLocalDate(
      this.state.startDate ? this.state.startDate * 1000
      : this.props.dataStartDate ? this.props.dataStartDate * 1000
      : undefined,
    );
    const endDateMax = getLocalDate(new Date());

    const startMinDate =
      isSet(this.props.dataStartDate) ? new Date(this.props.dataStartDate * 1000) : undefined;
    const todayMaxDate = new Date();
    const endMinDate =
      isSet(this.state.startDate) ? new Date(this.state.startDate * 1000)
      : isSet(this.props.dataStartDate) ? new Date(this.props.dataStartDate * 1000)
      : undefined;

    const onStartDateChange = (
      inputValue: string,
      parsedDate: Date | null,
      sourceZone: "utc" | "local" = "utc",
    ) => {
      if (inputValue === "") {
        this.setState({
          startDate: undefined,
          endDate: undefined,
          startDateInput: "",
          endDateInput: "",
          startDateError: this.context.formatMessage(messages.startDateRequired),
          endDateError: this.context.formatMessage(messages.endDateRequired),
        });
        return;
      }
      if (parsedDate === null) {
        // Invalid date format - clear the stale startDate (not just the text), since
        // canEnableUseFilterButton() only looks at state and would otherwise still let a
        // range through that no longer matches what's displayed
        this.setState({
          startDate: undefined,
          startDateInput: inputValue,
          startDateError: this.context.formatMessage(messages.startDateRequired),
        });
        return;
      }
      this.applyStartDate(parsedDate, inputValue, sourceZone);
    };

    // The end date is required for a custom range - clearing it isn't a valid "open-ended"
    // state (that's what the Day/Week/Month/Year buttons are for), just an incomplete one.
    const onEndDateChange = (
      inputValue: string,
      parsedDate: Date | null,
      sourceZone: "utc" | "local" = "utc",
    ) => {
      if (inputValue === "") {
        this.setState({
          endDate: undefined,
          endDateInput: "",
          endDateError: this.context.formatMessage(messages.endDateRequired),
        });
        return;
      }
      if (parsedDate === null) {
        // Invalid date format - clear the stale endDate (not just the text), since
        // canEnableUseFilterButton() only looks at state and would otherwise still let a
        // range through that no longer matches what's displayed
        this.setState({
          endDate: undefined,
          endDateInput: inputValue,
          endDateError: this.context.formatMessage(messages.endDateRequired),
        });
        return;
      }
      this.applyEndDate(parsedDate, inputValue, sourceZone);
    };

    return (
      <div className="customTime">
        <div className="customTimeField">
          <span>
            <FormattedMessage id="From" />
          </span>
          {isSafari ?
            <InlineDatePicker
              id="startDate"
              value={this.state.startDateInput}
              selectedDate={
                isSet(this.state.startDate) ? new Date(this.state.startDate * 1000) : undefined
              }
              minDate={startMinDate}
              maxDate={todayMaxDate}
              hasError={
                !this.state.startDate ||
                isSet(this.state.startDateError) ||
                this.isInvalidDateText(this.state.startDateInput)
              }
              title="Select start date"
              onTextChange={(inputValue) =>
                onStartDateChange(inputValue, this.parseIsoDateInput(inputValue))
              }
              onSelectDate={(date) => onStartDateChange(getLocalDate(date), date, "local")}
              onClear={() => onStartDateChange("", null)}
            />
          : <input
              id="startDate"
              type="date"
              className={`dateinput ${this.state.startDate ? "" : "error"}`}
              value={this.state.startDateInput}
              min={startDateMin}
              max={startDateMax}
              onChange={(event) => {
                const inputValue = event.currentTarget.value;
                onStartDateChange(inputValue, event.currentTarget.valueAsDate);
              }}
              title="Select start date"
              placeholder="YYYY-MM-DD"
            />
          }
          <div className="input-error-message">{this.state.startDateError || ""}</div>
        </div>
        <div className="customTimeField">
          <span>
            <FormattedMessage id="To" />
          </span>
          {isSafari ?
            <InlineDatePicker
              id="endDate"
              value={this.state.endDateInput}
              selectedDate={
                isSet(this.state.endDate) ? new Date(this.state.endDate * 1000) : undefined
              }
              minDate={endMinDate}
              maxDate={todayMaxDate}
              hasError={
                isSet(this.state.endDateError) || this.isInvalidDateText(this.state.endDateInput)
              }
              title="Select end date"
              onTextChange={(inputValue) =>
                onEndDateChange(inputValue, this.parseIsoDateInput(inputValue))
              }
              onSelectDate={(date) => onEndDateChange(getLocalDate(date), date, "local")}
              onClear={() => onEndDateChange("", null)}
            />
          : <input
              id="endDate"
              type="date"
              className={"dateinput"}
              value={this.state.endDateInput}
              min={endDateMin}
              max={endDateMax}
              onChange={(event) => {
                const inputValue = event.currentTarget.value;
                onEndDateChange(inputValue, event.currentTarget.valueAsDate);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && this.canEnableUseFilterButton()) {
                  this.activateCustomFilter();
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              title="Select end date"
              placeholder="YYYY-MM-DD"
            />
          }
          <div className="input-error-message">{this.state.endDateError || ""}</div>
        </div>
      </div>
    );
  }

  private renderDateFields() {
    return this.state.timePeriod === TimePeriod.CUSTOM ?
        this.renderCustomDateFields()
      : this.renderSingleDateField();
  }

  // Read-only info summary below the date field(s) - never selectable, just reflects what
  // will be applied: the resulting aggregation, plus a couple of period-specific hints.
  // Mirrors the boundary guiReducer enforces once a period is actually accepted (see
  // deriveNewGraphStartDate in guireducer.ts): trend data older than aggregationBoundary is
  // only kept at monthly resolution, so a Day/Week selection that far back is silently bumped
  // to Month there. Reflected here purely so the summary previews what will actually happen.
  private getEffectiveAggregationPeriod(timePeriod: TimePeriod, startDate: number): TimePeriod {
    const { aggregationBoundary } = this.props;
    if (
      isSet(aggregationBoundary) &&
      startDate < aggregationBoundary &&
      (timePeriod === TimePeriod.DAY || timePeriod === TimePeriod.WEEK)
    ) {
      return TimePeriod.MONTH;
    }
    return timePeriod;
  }

  private renderSummary() {
    const { timePeriod, startDate, endDate } = this.state;
    if (!isSet(startDate)) {
      return null;
    }

    const effectiveTimePeriod = this.getEffectiveAggregationPeriod(timePeriod, startDate);
    const aggregation = TrendWindowModel.getAggregation(
      effectiveTimePeriod,
      startDate,
      endDate,
      this.props.aggregationBoundary,
    );
    const aggregationName =
      aggregation === Aggregation.NONE ? "none" : getAggregationTimeUnitUINames(aggregation);
    const isDowngraded = effectiveTimePeriod !== timePeriod;

    // Custom keeps the user's exact range (it never gets switched to a different period like
    // Day/Week do above), but old ranges are restricted to daily aggregates
    const isCustomDowngraded =
      timePeriod === TimePeriod.CUSTOM &&
      aggregation !== TrendWindowModel.getAggregation(timePeriod, startDate, endDate, undefined);

    let weekRangeText: string | undefined;
    if (timePeriod === TimePeriod.WEEK) {
      const weekEnd = Math.round(DateTime.fromSeconds(startDate).plus({ weeks: 1 }).toSeconds());
      const weekStartText = getDisplayDate(startDate * 1000, this.context.locale);
      const weekEndText = getDisplayDate((weekEnd - 1) * 1000, this.context.locale);
      weekRangeText = `${weekStartText} – ${weekEndText}`;
    }

    let daysCount: number | undefined;
    if (timePeriod === TimePeriod.CUSTOM) {
      const start = DateTime.fromSeconds(startDate);
      const end = DateTime.fromSeconds(endDate ?? this.getStartOfNextDay(DateTime.now()));
      daysCount = Math.round(end.diff(start).as("days"));
    }

    return (
      <div className="periodSummary">
        {this.props.identifier === "trendPeriod" && (
          <div className="periodSummaryRow">
            <FormattedMessage id="Aggregation used" />
            {": "}
            <FormattedMessage id={`Period_${aggregationName}`} />
          </div>
        )}
        {weekRangeText !== undefined && (
          <div className="periodSummaryRow">
            <FormattedMessage id="Selected week" />
            {`: ${weekRangeText}`}
          </div>
        )}
        {daysCount !== undefined && (
          <div className="periodSummaryRow">
            {daysCount} <FormattedMessage id="days selected" />
          </div>
        )}
        {isDowngraded && (
          <div className="periodSummaryRow periodSummaryNote">
            <FormattedMessage id="Data this old is only available at daily resolution, so a full month is shown instead" />
          </div>
        )}
        {isCustomDowngraded && (
          <div className="periodSummaryRow periodSummaryNote">
            <FormattedMessage id="Data this old is only available at daily resolution" />
          </div>
        )}
      </div>
    );
  }

  // Quick filter
  private prepareCurrentQuickFilter(range: IQuickTimeRange) {
    const { start, end, period, customDaysSelected } = range.compute();
    const startDate = Math.round(start / 1000);
    const endDate = isSet(end) ? Math.round(end / 1000) : undefined;
    const isCustom = period === TimePeriod.CUSTOM;

    // store internally....
    this.setState({
      timePeriod: period,
      startDate,
      endDate: isCustom ? endDate : undefined,
      startDateInput: isCustom ? getLocalDate(start) : this.formatInputForPeriod(period, startDate),
      endDateInput: isCustom ? this.getLastDay(endDate) : "",
      anchorDate: startDate,
    });

    this.props.onFilterChanged({
      identifier: this.props.identifier,
      command: ActionGraphCommand.SET_DATE,
      selectedDate: startDate,
      customDaysSelected: isCustom ? customDaysSelected : undefined,
      timePeriod: period,
    });
  }
}

const ModalTrendItem = connect(mapStateToProps, mapDispatchToProps)(ModalTimeFilterComponent);

export default ModalTrendItem;
